import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Users, Filter, Loader2, Plus, Pencil, Trash2, X } from "lucide-react";
import { getApiUrl } from "../../config/api";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import { normalizeRole } from "../../utils/authUtils";

interface UserRow {
  id: string;
  userCode?: string;
  name: string;
  email: string;
  role: string;
  jobTitle?: string;
  department?: string;
  nrcNumber?: string;
  dateEmployed?: string;
  employmentType?: string;
  salaryStructure?: string;
  activeStatus?: number | boolean;
  hasLoginAccess?: number | boolean;
  phone?: string;
  title?: string;
  bankName?: string;
  bankAccountNumber?: string;
  transportRouteId?: string;
  transportMode?: string;
  currentClassId?: string | number | null;
  className?: string | null;
  teacherClassId?: string | number | null;
  teacherClassName?: string | null;
  teacherAssignmentRole?: string | null;
  baseSalary?: string | number;
  housingAllowance?: string | number;
  transportAllowance?: string | number;
  responsibilityAllowance?: string | number;
  otherAllowances?: string | number;
}

interface ClassOption {
  id: number;
  name: string;
}

const ROLE_FILTERS = [
  { key: "all", label: "All Staff" },
  { key: "teaching", label: "Teaching Staff" },
  { key: "administrative", label: "Administrative Staff" },
  { key: "workers", label: "Non-Teaching Workers" }
];

const WORKER_ROLE_OPTIONS = [
  "Driver",
  "Cook",
  "Cleaner",
  "Security Guard",
  "Librarian",
  "Gardener",
  "Office Assistant",
  "Maintenance Staff"
];

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [transportRoutes, setTransportRoutes] = useState<any[]>([]);
  const [availableClasses, setAvailableClasses] = useState<ClassOption[]>([]);
  const [workerRoleOptions, setWorkerRoleOptions] = useState<string[]>(WORKER_ROLE_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRow | null>(null);
  const [showWorkerModal, setShowWorkerModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    role: "",
    jobTitle: "",
    department: "",
    nrcNumber: "",
    dateEmployed: "",
    employmentType: "",
    salaryStructure: "",
    activeStatus: true,
    hasLoginAccess: true,
    phone: "",
    title: "",
    bankName: "",
    bankAccountNumber: "",
    password: "",
    currentClassId: "",
    teacherClassId: "",
    teacherAssignmentRole: "class_teacher",
    transportMode: "personal",
    transportRouteId: "",
    baseSalary: "",
    housingAllowance: "0",
    transportAllowance: "0",
    responsibilityAllowance: "0",
    otherAllowances: "0"
  });
  const [workerForm, setWorkerForm] = useState({
    fullName: "",
    workerRole: WORKER_ROLE_OPTIONS[0],
    department: "",
    phoneNumber: "",
    nrcNumber: "",
    dateEmployed: "",
    employmentType: "Permanent",
    salaryStructure: "Monthly",
    baseSalary: "",
    housingAllowance: "0",
    transportAllowance: "0",
    responsibilityAllowance: "0",
    otherAllowances: "0",
    activeStatus: true
  });
  const [actionLoading, setActionLoading] = useState(false);
  const token = localStorage.getItem("token") || "";

  useAutoDismiss(error, setError, "", 5000);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(getApiUrl("users/all"), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsers(res.data?.users || []);
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to load users.");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchUsers();
    }
  }, [token]);

  useEffect(() => {
    const fetchWorkerRoles = async () => {
      try {
        const res = await axios.get(getApiUrl("users/worker-role-options"), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWorkerRoleOptions(res.data?.roles || WORKER_ROLE_OPTIONS);
      } catch {
        setWorkerRoleOptions(WORKER_ROLE_OPTIONS);
      }
    };

    if (token) {
      fetchWorkerRoles();
    }
  }, [token]);

  useEffect(() => {
    const fetchTransportRoutes = async () => {
      try {
        const res = await axios.get(getApiUrl("transport/routes"), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTransportRoutes(res.data?.routes || []);
      } catch {
        setTransportRoutes([]);
      }
    };

    if (token) {
      fetchTransportRoutes();
    }
  }, [token]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await axios.get(getApiUrl("academics/classes"), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAvailableClasses(res.data?.classes || []);
      } catch {
        setAvailableClasses([]);
      }
    };

    if (token) {
      fetchClasses();
    }
  }, [token]);

  const filteredUsers = useMemo(() => {
    const roleOrder: Record<string, number> = {
      admin: 0,
      headteacher: 1,
      teacher: 2,
      worker: 3
    };
    const withOrder = (list: UserRow[]) =>
      [...list].sort((a, b) => {
        const aRole = normalizeRole(a.role);
        const bRole = normalizeRole(b.role);
        const aRank = roleOrder[aRole] ?? 99;
        const bRank = roleOrder[bRole] ?? 99;
        if (aRank !== bRank) return aRank - bRank;
        return a.name.localeCompare(b.name);
      });

    const employeesOnly = users.filter((u) => normalizeRole(u.role) !== "student");
    if (activeFilter === "all") return withOrder(employeesOnly);
    if (activeFilter === "teaching") {
      return withOrder(employeesOnly.filter((u) => normalizeRole(u.role) === "teacher"));
    }
    if (activeFilter === "administrative") {
      return withOrder(employeesOnly.filter((u) => ["admin", "headteacher"].includes(normalizeRole(u.role))));
    }
    if (activeFilter === "workers") {
      return withOrder(employeesOnly.filter((u) => normalizeRole(u.role) === "worker"));
    }
    return withOrder(employeesOnly);
  }, [users, activeFilter]);

  const getRoleBadgeClass = (role: string) => {
    const normalized = normalizeRole(role);
    switch (normalized) {
      case "teacher":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "headteacher":
        return "bg-purple-50 text-purple-700 border-purple-200";
      case "student":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "admin":
        return "bg-rose-50 text-rose-700 border-rose-200";
      case "worker":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      default:
        return "bg-slate-50 text-slate-600 border-slate-200";
    }
  };

  const getStaffGroupLabel = (user: UserRow) => {
    const role = normalizeRole(user.role);
    if (role === "teacher") return "Teaching Staff";
    if (role === "worker") return "Non-Teaching Worker";
    return "Administrative Staff";
  };

  const handleEditOpen = (user: UserRow) => {
    setEditingUser(user);
    setShowUserModal(true);
    setEditForm({
      name: user.name || "",
      email: user.email || "",
      role: normalizeRole(user.role) || "",
      jobTitle: user.jobTitle || "",
      department: user.department || "",
      nrcNumber: user.nrcNumber || "",
      dateEmployed: user.dateEmployed ? String(user.dateEmployed).slice(0, 10) : "",
      employmentType: user.employmentType || "",
      salaryStructure: user.salaryStructure || "",
      activeStatus: Boolean(user.activeStatus ?? true),
      hasLoginAccess: Boolean(user.hasLoginAccess ?? true),
      phone: user.phone || "",
      title: (user as any).title || "",
      bankName: user.bankName || "",
      bankAccountNumber: user.bankAccountNumber || "",
      password: "",
      currentClassId: user.currentClassId ? String(user.currentClassId) : "",
      teacherClassId: user.teacherClassId ? String(user.teacherClassId) : "",
      teacherAssignmentRole: user.teacherAssignmentRole || "class_teacher",
      transportMode: (user as any).transportMode || "personal",
      transportRouteId: (user as any).transportRouteId || "",
      baseSalary: (user as any).baseSalary?.toString?.() || "",
      housingAllowance: (user as any).housingAllowance?.toString?.() || "0",
      transportAllowance: (user as any).transportAllowance?.toString?.() || "0",
      responsibilityAllowance: (user as any).responsibilityAllowance?.toString?.() || "0",
      otherAllowances: (user as any).otherAllowances?.toString?.() || "0"
    });
  };

  const openCreateUserModal = () => {
    setEditingUser(null);
    setShowUserModal(true);
    setEditForm({
      name: "",
      email: "",
      role: "teacher",
      jobTitle: "",
      department: "",
      nrcNumber: "",
      dateEmployed: "",
      employmentType: "",
      salaryStructure: "",
      activeStatus: true,
      hasLoginAccess: true,
      phone: "",
      title: "",
      bankName: "",
      bankAccountNumber: "",
      password: "",
      currentClassId: "",
      teacherClassId: "",
      teacherAssignmentRole: "class_teacher",
      transportMode: "personal",
      transportRouteId: "",
      baseSalary: "",
      housingAllowance: "0",
      transportAllowance: "0",
      responsibilityAllowance: "0",
      otherAllowances: "0"
    });
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setEditingUser(null);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "role") {
      setEditForm((prev) => ({
        ...prev,
        role: value,
        currentClassId: value === "student" ? prev.currentClassId : "",
        teacherClassId: value === "teacher" ? prev.teacherClassId : "",
        teacherAssignmentRole: value === "teacher" ? prev.teacherAssignmentRole : "class_teacher",
        transportMode: value === "student" ? prev.transportMode : "personal",
        transportRouteId: value === "student" ? prev.transportRouteId : ""
      }));
      return;
    }
    if (name === "transportRouteId") {
      setEditForm((prev) => ({
        ...prev,
        transportRouteId: value,
        transportMode: value ? "school" : "personal"
      }));
      return;
    }
    setEditForm({ ...editForm, [name]: value });
  };

  const handleWorkerFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setWorkerForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    setError("");
    try {
      const payload = {
        name: editForm.name.trim(),
        title: editForm.title.trim(),
        email: editForm.email.trim(),
        role: editForm.role,
        jobTitle: editForm.jobTitle.trim(),
        department: editForm.department.trim(),
        nrcNumber: editForm.nrcNumber.trim(),
        dateEmployed: editForm.dateEmployed || null,
        employmentType: editForm.employmentType || null,
        salaryStructure: editForm.salaryStructure || null,
        activeStatus: editForm.activeStatus,
        hasLoginAccess: editForm.hasLoginAccess,
        phone: editForm.phone.trim(),
        bankName: editForm.bankName.trim(),
        bankAccountNumber: editForm.bankAccountNumber.trim(),
        password: editForm.password.trim() || undefined,
        currentClassId: editForm.role === "student" ? (editForm.currentClassId || null) : null,
        teacherClassId: editForm.role === "teacher" ? (editForm.teacherClassId || null) : null,
        teacherAssignmentRole: editForm.role === "teacher" ? editForm.teacherAssignmentRole : null,
        transportMode: editForm.transportMode,
        transportRouteId: editForm.transportRouteId || null,
        baseSalary: editForm.baseSalary,
        housingAllowance: editForm.housingAllowance,
        transportAllowance: editForm.transportAllowance,
        responsibilityAllowance: editForm.responsibilityAllowance,
        otherAllowances: editForm.otherAllowances
      };

      if (editingUser) {
        await axios.put(
          getApiUrl(`users/${editingUser.id}`),
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          getApiUrl("auth/signup"),
          {
            ...payload,
            password: editForm.password.trim()
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      const res = await axios.get(getApiUrl("users/all"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data?.users || []);
      closeUserModal();
    } catch (err: any) {
      setError(err.response?.data?.message || (editingUser ? "Failed to update user." : "Failed to create user."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateWorker = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    setError("");
    try {
      await axios.post(
        getApiUrl("users/workers"),
        workerForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const res = await axios.get(getApiUrl("users/all"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data?.users || []);
      setShowWorkerModal(false);
      setWorkerForm({
        fullName: "",
        workerRole: workerRoleOptions[0] || WORKER_ROLE_OPTIONS[0],
        department: "",
        phoneNumber: "",
        nrcNumber: "",
        dateEmployed: "",
        employmentType: "Permanent",
        salaryStructure: "Monthly",
        baseSalary: "",
        housingAllowance: "0",
        transportAllowance: "0",
        responsibilityAllowance: "0",
        otherAllowances: "0",
        activeStatus: true
      });
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to create worker.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (user: UserRow) => {
    if (!window.confirm(`Delete ${user.name}? This cannot be undone.`)) return;
    setActionLoading(true);
    setError("");
    try {
      await axios.delete(getApiUrl(`users/${user.id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to delete user.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/30 p-6" style={{ fontFamily: '"Segoe UI", "Source Sans 3", "Helvetica Neue", Arial, sans-serif' }}>
      <div className="w-full">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
            <Users className="text-blue-600" size={22} />
          </div>
          <div>
            <h1 className="text-3xl font-black text-slate-800">Users</h1>
            <p className="text-slate-700 mt-1 text-base font-bold">Browse and filter user accounts.</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/40 p-6 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-slate-600 text-base font-bold">
              <Filter size={16} />
              Filter:
            </div>
            {ROLE_FILTERS.map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-4 py-2 rounded-full text-base font-bold border transition-all ${
                  activeFilter === filter.key
                    ? "bg-teal-600 text-white border-teal-600 shadow-sm"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {filter.label}
              </button>
            ))}
            <button
              onClick={openCreateUserModal}
              className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full text-base font-bold border border-teal-600 text-teal-700 hover:bg-teal-50 transition-all"
            >
              <Plus size={16} />
              Create User
            </button>
            <button
              onClick={() => setShowWorkerModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-base font-bold border border-emerald-600 text-emerald-700 hover:bg-emerald-50 transition-all"
            >
              <Plus size={16} />
              Add Worker
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-700 rounded-xl p-4 mb-6">
            {error}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/40 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-blue-600" size={32} />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No users found for this filter.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="py-3 px-3 text-base font-black text-slate-600 uppercase tracking-wider">Name</th>
                    <th className="py-3 px-3 text-base font-black text-slate-600 uppercase tracking-wider">Role</th>
                    <th className="py-3 px-3 text-base font-black text-slate-600 uppercase tracking-wider">Category</th>
                    <th className="py-3 px-3 text-base font-black text-slate-600 uppercase tracking-wider">Email</th>
                    <th className="py-3 px-3 text-base font-black text-slate-600 uppercase tracking-wider">Phone</th>
                    <th className="py-3 px-3 text-base font-black text-slate-600 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                      <td className="py-3 px-3">
                        <div className="font-black text-slate-800 text-base">{user.name}</div>
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-1">{user.userCode || `UID-${user.id}`}</div>
                      </td>
                      <td className="py-3 px-3">
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full border text-xs font-black uppercase tracking-wide ${getRoleBadgeClass(user.role)}`}>
                          {normalizeRole(user.role) === "worker" ? (user.jobTitle || "worker") : (normalizeRole(user.role) || "user")}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-slate-700 text-sm font-bold">{getStaffGroupLabel(user)}</td>
                      <td className="py-3 px-3 text-slate-800 text-base font-bold">
                        {normalizeRole(user.role) === "worker" && !user.hasLoginAccess ? "No login account" : user.email}
                      </td>
                      <td className="py-3 px-3 text-slate-800 text-base font-bold">{user.phone || "-"}</td>
                      <td className="py-3 px-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => handleEditOpen(user)}
                            className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-teal-50 hover:text-teal-700 transition-all"
                            title="Edit"
                            disabled={actionLoading}
                          >
                            <Pencil size={16} />
                          </button>
                          {normalizeRole(user.role) === "admin" ? null : (
                            <button
                              onClick={() => handleDelete(user)}
                              className="p-2 rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50 transition-all"
                              title="Delete user (confirmation required)"
                              disabled={actionLoading}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showUserModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-xl p-6 shadow-xl max-h-[90vh] overflow-y-auto my-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-800">{editingUser ? "Edit User" : "Create User"}</h2>
              <button
                onClick={closeUserModal}
                className="p-2 rounded-full hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Full Name</label>
                <input
                  name="name"
                  value={editForm.name}
                  onChange={handleEditChange}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">User Role</label>
                <select
                  name="role"
                  value={editForm.role}
                  onChange={handleEditChange}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200"
                  required
                >
                  <option value="teacher">Teacher</option>
                  <option value="headteacher">Headteacher</option>
                  <option value="admin">Administrator</option>
                  <option value="worker">Worker</option>
                </select>
              </div>
              {editForm.role === "worker" && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Worker Role</label>
                    <select
                      name="jobTitle"
                      value={editForm.jobTitle}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                    >
                      {workerRoleOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Department</label>
                    <input
                      name="department"
                      value={editForm.department}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">NRC / National ID</label>
                    <input
                      name="nrcNumber"
                      value={editForm.nrcNumber}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Date Employed</label>
                    <input
                      name="dateEmployed"
                      type="date"
                      value={editForm.dateEmployed}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Employment Type</label>
                    <input
                      name="employmentType"
                      value={editForm.employmentType}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Salary Structure</label>
                    <input
                      name="salaryStructure"
                      value={editForm.salaryStructure}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Active Status</label>
                    <select
                      name="activeStatus"
                      value={editForm.activeStatus ? "active" : "inactive"}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, activeStatus: e.target.value === "active" }))}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Portal Access</label>
                    <select
                      name="hasLoginAccess"
                      value={editForm.hasLoginAccess ? "enabled" : "disabled"}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, hasLoginAccess: e.target.value === "enabled" }))}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                    >
                      <option value="disabled">No Login Account</option>
                      <option value="enabled">Login Enabled</option>
                    </select>
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">
                  {editForm.role === "student" ? "Gender" : "Title"}
                </label>
                <input
                  name="title"
                  value={editForm.title}
                  onChange={handleEditChange}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200"
                  placeholder={editForm.role === "student" ? "Male or Female" : "Mr, Ms, Mrs, Miss..."}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Email</label>
                <input
                  name="email"
                  type="email"
                  value={editForm.email}
                  onChange={handleEditChange}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200"
                  required={!editingUser || editForm.role !== "worker"}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Phone</label>
                <input
                  name="phone"
                  value={editForm.phone}
                  onChange={handleEditChange}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200"
                />
              </div>
              {editForm.role === "student" && (
                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-1">Class Assignment</label>
                  <select
                    name="currentClassId"
                    value={editForm.currentClassId}
                    onChange={handleEditChange}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200"
                  >
                    <option value="">No class assigned yet</option>
                    {availableClasses.map((cls) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {editForm.role === "teacher" && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Teaching Class</label>
                    <select
                      name="teacherClassId"
                      value={editForm.teacherClassId}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                    >
                      <option value="">No class assigned yet</option>
                      {availableClasses.map((cls) => (
                        <option key={cls.id} value={cls.id}>
                          {cls.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {editForm.teacherClassId && (
                    <div>
                      <label className="block text-sm font-semibold text-slate-600 mb-1">Class Role</label>
                      <select
                        name="teacherAssignmentRole"
                        value={editForm.teacherAssignmentRole}
                        onChange={handleEditChange}
                        className="w-full px-4 py-2 rounded-lg border border-slate-200"
                      >
                        <option value="class_teacher">Class Teacher</option>
                        <option value="assistant_teacher">Assistant Teacher</option>
                      </select>
                    </div>
                  )}
                </>
              )}
              {editForm.role === "student" && (
                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-1">Transport Route</label>
                  <select
                    name="transportRouteId"
                    value={editForm.transportRouteId}
                    onChange={handleEditChange}
                    className="w-full px-4 py-2 rounded-lg border border-slate-200"
                  >
                    <option value="">Personal (No school transport)</option>
                    {transportRoutes.map((route) => (
                      <option key={route.id} value={route.id}>
                        {route.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {editForm.role !== "student" && editForm.role !== "worker" && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Job Title</label>
                    <input
                      name="jobTitle"
                      value={editForm.jobTitle}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                      placeholder="Job title"
                    />
                  </div>
                </>
              )}
              {editForm.role !== "student" && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Bank Name</label>
                    <input
                      name="bankName"
                      value={editForm.bankName}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                      placeholder="Enter bank name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Bank Account Number</label>
                    <input
                      name="bankAccountNumber"
                      value={editForm.bankAccountNumber}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                      placeholder="Enter account number"
                    />
                  </div>
                </>
              )}
              {editForm.role !== "student" && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Base Salary</label>
                    <input
                      name="baseSalary"
                      type="number"
                      value={editForm.baseSalary}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Housing Allowance</label>
                    <input
                      name="housingAllowance"
                      type="number"
                      value={editForm.housingAllowance}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Transport Allowance</label>
                    <input
                      name="transportAllowance"
                      type="number"
                      value={editForm.transportAllowance}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Responsibility Allowance</label>
                    <input
                      name="responsibilityAllowance"
                      type="number"
                      value={editForm.responsibilityAllowance}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1">Other Allowances</label>
                    <input
                      name="otherAllowances"
                      type="number"
                      value={editForm.otherAllowances}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 rounded-lg border border-slate-200"
                      placeholder="0.00"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Password (Optional)</label>
                <input
                  name="password"
                  type="password"
                  value={editForm.password}
                  onChange={handleEditChange}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200"
                  placeholder={editingUser ? "Leave blank to keep current password" : "Enter password"}
                  required={!editingUser}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeUserModal}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60"
                >
                  {actionLoading ? "Saving..." : editingUser ? "Update" : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showWorkerModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-xl max-h-[90vh] overflow-y-auto my-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-800">Add Non-Teaching Worker</h2>
              <button onClick={() => setShowWorkerModal(false)} className="p-2 rounded-full hover:bg-slate-100">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleCreateWorker} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Full Name</label>
                <input name="fullName" value={workerForm.fullName} onChange={handleWorkerFormChange} className="w-full px-4 py-2 rounded-lg border border-slate-200" required />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Worker Role</label>
                <select name="workerRole" value={workerForm.workerRole} onChange={handleWorkerFormChange} className="w-full px-4 py-2 rounded-lg border border-slate-200">
                  {workerRoleOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Department</label>
                <input name="department" value={workerForm.department} onChange={handleWorkerFormChange} className="w-full px-4 py-2 rounded-lg border border-slate-200" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Phone Number</label>
                <input name="phoneNumber" value={workerForm.phoneNumber} onChange={handleWorkerFormChange} className="w-full px-4 py-2 rounded-lg border border-slate-200" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">NRC / National ID</label>
                <input name="nrcNumber" value={workerForm.nrcNumber} onChange={handleWorkerFormChange} className="w-full px-4 py-2 rounded-lg border border-slate-200" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Date Employed</label>
                <input type="date" name="dateEmployed" value={workerForm.dateEmployed} onChange={handleWorkerFormChange} className="w-full px-4 py-2 rounded-lg border border-slate-200" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Employment Type</label>
                <input name="employmentType" value={workerForm.employmentType} onChange={handleWorkerFormChange} className="w-full px-4 py-2 rounded-lg border border-slate-200" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Salary Structure</label>
                <input name="salaryStructure" value={workerForm.salaryStructure} onChange={handleWorkerFormChange} className="w-full px-4 py-2 rounded-lg border border-slate-200" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Base Salary</label>
                <input type="number" name="baseSalary" value={workerForm.baseSalary} onChange={handleWorkerFormChange} className="w-full px-4 py-2 rounded-lg border border-slate-200" required />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Housing Allowance</label>
                <input type="number" name="housingAllowance" value={workerForm.housingAllowance} onChange={handleWorkerFormChange} className="w-full px-4 py-2 rounded-lg border border-slate-200" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Transport Allowance</label>
                <input type="number" name="transportAllowance" value={workerForm.transportAllowance} onChange={handleWorkerFormChange} className="w-full px-4 py-2 rounded-lg border border-slate-200" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Responsibility Allowance</label>
                <input type="number" name="responsibilityAllowance" value={workerForm.responsibilityAllowance} onChange={handleWorkerFormChange} className="w-full px-4 py-2 rounded-lg border border-slate-200" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Other Allowances</label>
                <input type="number" name="otherAllowances" value={workerForm.otherAllowances} onChange={handleWorkerFormChange} className="w-full px-4 py-2 rounded-lg border border-slate-200" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1">Status</label>
                <select
                  name="activeStatus"
                  value={workerForm.activeStatus ? "active" : "inactive"}
                  onChange={(e) => setWorkerForm((prev) => ({ ...prev, activeStatus: e.target.value === "active" }))}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowWorkerModal(false)} className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" disabled={actionLoading} className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60">
                  {actionLoading ? "Saving..." : "Create Worker"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
